import { useContext, useMemo } from 'react';
import { AuthContext } from '../../providers/AuthProvider';
import { PlanFeatures, PlanLimits, VendorPlan, VendorPlanOverrides } from '../../modules/superadmin/our-vendors/models/vendor.model';
import { NewLimitKey, isFreePlan, readLimit } from '../../components/design-system/plan/plan-limits';
import { ContractSummary, isTrialPlan, readAccess } from '../../components/design-system/plan/access';

const DEFAULT_FEATURES: PlanFeatures = {
  apiIntegration: true,
  emailAutomation: true,
  aiSpaceAnalysis: true,
  seamlessGeneration: true,
  model3dGeneration: true,
  advancedAnalytics: true,
  customFields: true,
  categoryManagement: true,
  productCardCustomization: true,
  contentStudio: true,
  socialScheduler: true,
  shopperChat: true,
  adsManager: true,
  // The one exception to the permissive fallback: this is not a UI gate but a
  // spend switch, decided server-side, and "plan not loaded yet" must never
  // read as "vision analysis is on".
  aiProductEnrichment: false,
  visualizationQuality: 'high',
};

const DEFAULT_LIMITS: PlanLimits = {
  maxProducts: -1,
  maxSpaces: -1,
  maxEmailsPerMonth: -1,
  maxSyncFlowsPerDay: -1,
  maxAutomationRules: -1,
  maxLeads: -1,
  maxImportProducts: -1,
  monthlyCredits: -1,
  maxSocialAccounts: -1,
  maxScheduledPostsPerMonth: -1,
  maxVisualizationsPerMonth: -1,
  maxSeats: -1,
};

export const useVendorPlan = () => {
  // Outside the provider (a test, a story) there is no user: read that as "no plan", not a crash.
  const user = useContext(AuthContext)?.user;
  const plan = user?.vendor?.plan;
  const overrides: VendorPlanOverrides | undefined = user?.vendor?.planOverrides;

  // The vendor's own overrides win, as they do server side (applyPlanOverrides):
  // a feature granted to one vendor is on for that vendor whatever the plan says.
  const featureOverrides = overrides?.features;
  const features = useMemo((): PlanFeatures => {
    const base = plan?.features || DEFAULT_FEATURES;
    const set = Object.entries(featureOverrides || {}).filter(([, v]) => v !== undefined && v !== null);
    return set.length ? { ...base, ...Object.fromEntries(set) } : base;
  }, [plan, featureOverrides]);

  const limits = useMemo((): PlanLimits => {
    const planLimits = plan?.limits || DEFAULT_LIMITS;
    return {
      ...planLimits,
      // Vendor-level overrides take precedence
      maxProducts: user?.vendor?.maxProducts ?? planLimits.maxProducts,
      maxSpaces: user?.vendor?.maxSpaces ?? planLimits.maxSpaces,
    };
  }, [plan, user?.vendor?.maxProducts, user?.vendor?.maxSpaces]);

  const hasFeature = (feature: keyof PlanFeatures): boolean => {
    return features[feature] as boolean ?? false;
  };

  /**
   * A plan limit; -1 is unlimited. The free plan's newer keys (row caps, monthly
   * meters, history window) are absent on plans saved before them, and absent
   * reads as unlimited — never as zero.
   */
  const getLimit = (limitKey: keyof PlanLimits | NewLimitKey): number => {
    return readLimit((limits as unknown as Record<string, unknown>)[limitKey]);
  };

  const isFeatureLocked = (feature: keyof PlanFeatures): boolean => {
    return !hasFeature(feature);
  };

  /** Check if a limit-based feature is disabled (limit === 0) */
  const isLimitDisabled = (limitKey: keyof PlanLimits | NewLimitKey): boolean => {
    return getLimit(limitKey) === 0;
  };

  // Stripe subscription state (from the /me projection). The Vendor type doesn't
  // declare these Stripe fields, so read them off `any`.
  const vendorAny = user?.vendor as any;
  const subscriptionStatus: string | undefined = vendorAny?.subscriptionStatus;
  const subscriptionCurrentPeriodEnd: string | undefined = vendorAny?.subscriptionCurrentPeriodEnd;
  const hasActiveSubscription =
    !!vendorAny?.stripeSubscriptionId && subscriptionStatus === 'active';

  // Wire-transfer schedule: what a vendor paying by transfer owes next, known even
  // before any invoice exists (an imported legacy customer).
  const paymentMethod: 'wire' | 'stripe' | undefined = vendorAny?.paymentMethod || undefined;
  const wire =
    paymentMethod === 'wire' && vendorAny?.wireCurrentPeriodEnd && vendorAny?.wirePriceAmount > 0
      ? {
          amount: vendorAny.wirePriceAmount as number,
          interval: (vendorAny.wireInterval === 'year' ? 'year' : 'month') as 'month' | 'year',
          periodEnd: vendorAny.wireCurrentPeriodEnd as string,
        }
      : undefined;

  // Trial / lapsed state and the contract commitment (docs/pricing-v2.md).
  const access = readAccess(user?.vendor);
  const contract: ContractSummary | null = vendorAny?.contract ?? null;
  const isTrial = isTrialPlan(plan) && access.state !== 'expired';

  return {
    plan,
    overrides,
    planName: plan?.name || 'Enterprise',
    planSlug: plan?.slug || 'enterprise',
    /** On a no-charge tier (Free, or a running Trial): every feature open, depth limited. */
    isFree: isFreePlan(plan) || isTrial,
    access,
    isTrial,
    isExpired: access.state === 'expired',
    contract,
    features,
    limits,
    hasFeature,
    getLimit,
    isFeatureLocked,
    isLimitDisabled,
    subscriptionStatus,
    subscriptionCurrentPeriodEnd,
    hasActiveSubscription,
    wire,
  };
};
